import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * TokenManager — safe Supabase JWT accessor with a refresh mutex.
 *
 * Supabase refresh tokens are NOT idempotent: if two callers fire
 * `refreshSession()` concurrently, the second call arrives with a
 * token that has already been rotated and will receive an error.
 * This class coalesces all simultaneous refresh requests into a
 * single in-flight Promise so the rotation only happens once.
 */
export class TokenManager {
  /**
   * The single in-flight refresh promise, if a refresh is currently running.
   * Cleared in the `finally` block so the next call can start a fresh refresh.
   */
  private refreshPromise: Promise<string | null> | null = null;

  constructor(private supabase: SupabaseClient) {}

  /**
   * Returns the current access token, refreshing only when the session
   * carries no token (expired or absent).
   */
  async getToken(): Promise<string | null> {
    const { data } = await this.supabase.auth.getSession();
    const token = data.session?.access_token ?? null;

    if (token !== null) {
      return token;
    }

    // No token in the current session — attempt a refresh.
    return this.refresh();
  }

  /**
   * Forces a session refresh and returns the new access token.
   *
   * Concurrent callers receive the same Promise so that Supabase
   * `refreshSession()` is called exactly once per refresh cycle.
   */
  async refresh(): Promise<string | null> {
    if (this.refreshPromise !== null) {
      // Another caller is already refreshing — join their promise.
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const { data } = await this.supabase.auth.refreshSession();
        return data.session?.access_token ?? null;
      } finally {
        // Always clear the mutex so the next call can start a fresh refresh.
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }
}
